import { useCallback, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import i18n from 'modules/core/lib/i18n'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'

import TransactionsDetailsWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/TransactionsDetailsWidget'
import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import Files from 'modules/expenses/components/expenses/Detail/_newForm/Files'
import AdditionalInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/AdditionalInformation'
import BudgetAndProject from 'modules/expenses/components/expenses/Detail/_newForm/Sections/BudgetAndProject'
import CostCenterInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/CostCenters'
import DailyConcepts, {
  LocalConcept,
} from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/DailyConcepts'
import Destinations from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/Destinations'
import ExpenseInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/ExpenseInformation'
import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import PerDiemRates from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/PerDiemRates'
import TripInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/TripInformation'
import { isFieldEditable } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { buildConceptExclusionsByDate } from 'modules/expenses/components/expenses/New/PerDiemWizard/companyPaidUtils'
import DailyBreakdown from 'modules/expenses/components/expenses/New/PerDiemWizard/components/DailyBreakdown'
import { PerDiemDay } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { useCalculatePerDiem } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useCalculatePerDiem'
import { usePerDiemBreakdown } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/usePerDiemBreakdown'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { subcategoriesForCategory } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import {
  isAutomaticExpensable,
  isImmutableExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

import { ScopedEditSection } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

interface PerDiemFormProps {
  customizationFields: CustomizationField[]
  canSeeEmployeeLegalEntity: boolean
  canCreateExpensesForOthers: boolean
  canEditExpensesWithPendingStatus: boolean
  expensable?: Expensable
  hasProjectsFeature: boolean
  hasReportOnBehalfFeature: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  projectWorkers: ProjectWorker[]
  budgetOptions: BudgetOption[]
  loadingProjectWorkers: boolean
  loadingBudgetOptions: boolean
  onEmployeeIdChange: (employeeId: number | undefined) => void
  onEffectiveOnChange: (value: string) => void
  scrollTo?: string | undefined
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  isNewPerDiem?: boolean
  onConceptsModified?: (concepts: LocalConcept[]) => void
  editSection?: ScopedEditSection | undefined
  onBackToConfirmation?: () => void
}

const NewPerDiemForm = (props: PerDiemFormProps) => {
  const {
    hasProjectsFeature,
    expensable,
    taxAgencyOrganization,
    projectWorkers,
    budgetOptions,
    loadingProjectWorkers,
    loadingBudgetOptions,
    onEmployeeIdChange,
    onEffectiveOnChange,
    scrollTo,
    registerDropHandler,
    canCreateExpensesForOthers,
    customizationFields,
    isNewPerDiem = false,
    onConceptsModified,
    editSection,
    onBackToConfirmation,
  } = props

  const { section } = useOutletContext<RouteContextParams>()
  const asReviewer = !isMyExpensesSection(section)
  const { control } = useFormContext()

  const tripInformationRef = useRef<HTMLDivElement>(null)
  const perDiemRatesRef = useRef<HTMLDivElement>(null)
  const basicInformationRef = useRef<HTMLDivElement>(null)
  const companyPaidRef = useRef<HTMLDivElement>(null)
  const budgetAndProjectRef = useRef<HTMLDivElement>(null)
  const additionalInformationRef = useRef<HTMLDivElement>(null)
  const costCenterInformationRef = useRef<HTMLDivElement>(null)

  const subcategoryOptions = useMemo(
    () =>
      subcategoriesForCategory(customizationFields, 'perdiem', 'perdiem').map((option) => {
        return { label: option.label, value: option.value ?? '' }
      }),
    [customizationFields]
  )

  const hasSubcategory = subcategoryOptions.length > 0

  const {
    employee,
    access: { locale },
  } = useCurrentSession()
  const { watch, setValue } = useFormContext()

  const isTripSectionEditable =
    !expensable ||
    (employee &&
      isFieldEditable({
        expensable,
        asReviewer,
        canEditExpensesWithPendingStatus: props.canEditExpensesWithPendingStatus,
        currentEmployee: employee,
      }))

  // State for recalculated amounts and modified concepts
  const [recalculatedDays, setRecalculatedDays] = useState<Array<PerDiemDay | null> | null>(null)
  const [recalculatedTotalCents, setRecalculatedTotalCents] = useState<number | null>(null)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [modifiedConcepts, setModifiedConcepts] = useState<LocalConcept[] | null>(null)
  // Preserve original breakdown values when entering preview mode
  const [preservedBreakdownDays, setPreservedBreakdownDays] =
    useState<Array<PerDiemDay | null> | null>(null)
  const [preservedBreakdownTotalCents, setPreservedBreakdownTotalCents] = useState<number | null>(
    null
  )

  // Hooks for calculation
  const { calculate } = useCalculatePerDiem()

  // Fetch breakdown data for new per diem wizard
  // Only fetch if we don't have modified concepts (to avoid overwriting the preview)
  const shouldFetchBreakdown = !modifiedConcepts
  const {
    days: breakdownDays,
    isLoading: isBreakdownLoading,
    totalAmountCents: breakdownTotalAmountCents,
    currency: breakdownCurrency,
  } = usePerDiemBreakdown({
    expensable: shouldFetchBreakdown ? expensable : undefined,
    employeeId: shouldFetchBreakdown ? employee?.id : undefined,
  })

  // Preserve breakdown values when they're fetched from server
  useEffect(() => {
    if (shouldFetchBreakdown && !isBreakdownLoading) {
      if (breakdownDays) {
        setPreservedBreakdownDays(breakdownDays)
      }
      if (breakdownTotalAmountCents !== undefined && breakdownTotalAmountCents !== null) {
        setPreservedBreakdownTotalCents(breakdownTotalAmountCents)
      }
    }
  }, [shouldFetchBreakdown, isBreakdownLoading, breakdownDays?.length, breakdownTotalAmountCents])

  // Use recalculated values if available, otherwise use preserved or original breakdown
  const displayDays = recalculatedDays ?? preservedBreakdownDays ?? breakdownDays
  const displayTotalCents =
    recalculatedTotalCents ?? preservedBreakdownTotalCents ?? breakdownTotalAmountCents
  const displayIsLoading = (shouldFetchBreakdown && isBreakdownLoading) || isRecalculating

  const conceptExclusionsByDate = useMemo(
    () => buildConceptExclusionsByDate(modifiedConcepts ?? expensable?.perDiem?.perDiemConcepts),
    [modifiedConcepts, expensable?.perDiem?.perDiemConcepts]
  )

  // Handler for when concepts (company-paid expenses) change
  const handleConceptsChange = useCallback(
    async (concepts: LocalConcept[]) => {
      if (!expensable || !isNewPerDiem || !employee) return

      if (isImmutableExpensable(expensable)) {
        return
      }

      const perDiem = expensable.perDiem
      if (!perDiem) return

      const destinations = perDiem.perDiemDestinations?.map((dest) => ({
        id: String(dest.id),
        locationName: dest.locationName || '',
        arrivalDatetime: new Date(dest.arrivalDate),
        locationData: {
          country: dest.country || '',
          administrativeAreaLevel1: dest.administrativeAreaLevel1 ?? undefined,
          administrativeAreaLevel2: dest.administrativeAreaLevel2 ?? undefined,
          locality: dest.locality ?? undefined,
        },
      }))

      if (!destinations || destinations.length === 0) return

      const startDate = perDiem.startDate ? new Date(perDiem.startDate) : null
      const endDate = perDiem.endDate ? new Date(perDiem.endDate) : null

      if (!startDate || !endDate) return

      setModifiedConcepts(concepts)
      onConceptsModified?.(concepts)

      try {
        setIsRecalculating(true)

        const dayExclusions = concepts.map((concept) => ({
          date: new Date(concept.date),
          breakfastProvided: concept.breakfastProvided,
          lunchProvided: concept.lunchProvided,
          dinnerProvided: concept.dinnerProvided,
          accommodationProvided: concept.accommodationProvided,
        }))

        const calculationResult = await calculate({
          employeeId: expensable.owner?.id ?? employee.id,
          startDate,
          endDate,
          from: perDiem.from || undefined,
          destinations,
          dayExclusions,
        })

        // Update displayed breakdown with calculated results (preview only)
        if (calculationResult.days) {
          setRecalculatedDays(calculationResult.days ?? null)
        }
        if (calculationResult.totalAmountCents !== undefined) {
          setRecalculatedTotalCents(calculationResult.totalAmountCents)
          // Update the form's amount field so it's included in the save mutation
          setValue('amount', calculationResult.totalAmountCents, { shouldDirty: true })
        }

        setIsRecalculating(false)

        // NOTE: We do NOT persist to backend here anymore
        // Changes will be saved when user clicks "Save" or "Send for Review"
        // The modifiedConcepts state will be used by the save/send mutations
      } catch {
        setIsRecalculating(false)
      }
    },
    [expensable, isNewPerDiem, employee, calculate, onConceptsModified, setValue]
  )

  useEffect(() => {
    // The timeout gives a more graceful animation experience
    setTimeout(() => {
      let ref: HTMLDivElement | null = null
      switch (scrollTo) {
        case 'basicInformation':
          ref = basicInformationRef.current
          break
        case 'tripInformation':
          ref = tripInformationRef.current
          break
        case 'perDiemRates':
          ref = perDiemRatesRef.current
          break
        case 'companyPaid':
          ref = companyPaidRef.current
          break
        case 'budgetAndProject':
          ref = budgetAndProjectRef.current
          break
        case 'additionalInformation':
          ref = additionalInformationRef.current
          break
      }
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 300)
  }, [scrollTo])

  // TODO: Remove this once we have edited all the blocks
  const documentType = watch('documentType')

  const isNewExpense = !expensable?.id
  const isAutomatic = expensable ? isAutomaticExpensable(expensable) : false

  // Scoped edit mode: render only the target section + back link
  if (editSection && isNewPerDiem && onBackToConfirmation) {
    const backLink = (
      <Box
        onClick={onBackToConfirmation}
        flexDirection="row"
        alignItems="center"
        gap="s4"
        style={{ cursor: 'pointer' }}
      >
        <Text size="200" weight="medium" color="grey800">
          {`← ${i18n.t(
            'expenses_v2.confirmation_screen.expense.per_diem_summary_block.back_to_summary'
          )}`}
        </Text>
      </Box>
    )

    if (editSection === 'tripInformation') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <Destinations expensable={expensable} readOnly={!isTripSectionEditable} />
          <FormBlock
            title={i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_label')}
            subtitle={i18n.t(
              'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_placeholder'
            )}
          >
            <Controller
              control={control}
              name="perDiem.tripName"
              render={({ field }) => (
                <TextField
                  label={i18n.t(
                    'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_label'
                  )}
                  placeholder={i18n.t(
                    'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_placeholder'
                  )}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value || null)}
                  readonly={!isTripSectionEditable}
                />
              )}
            />
          </FormBlock>
        </Box>
      )
    }

    if (editSection === 'companyPaid') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <DailyConcepts
            expensable={expensable}
            onConceptsChange={handleConceptsChange}
            disabled={isRecalculating || !isTripSectionEditable}
          />
          <DailyBreakdown
            days={displayDays}
            totalAmountCents={displayTotalCents}
            currency={breakdownCurrency}
            locale={locale}
            isLoading={displayIsLoading}
            conceptExclusionsByDate={conceptExclusionsByDate}
          />
        </Box>
      )
    }

    if (editSection === 'basicInformation') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <ExpenseInformation
            {...props}
            currentEmployee={employee}
            resourceType={'perdiem'}
            asReviewer={asReviewer}
            onEmployeeIdChange={onEmployeeIdChange}
            isAutomaticExpensable={isAutomatic}
            documentType={documentType}
            onEffectiveOnChange={onEffectiveOnChange}
          />
        </Box>
      )
    }

    if (editSection === 'budgetAndProject') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <BudgetAndProject
            {...props}
            currentEmployee={employee}
            resourceType={'perdiem'}
            asReviewer={asReviewer}
            documentType={documentType}
            budgetOptions={budgetOptions}
            projectWorkers={projectWorkers}
            hasProjectsFeature={hasProjectsFeature}
            loadingProjectWorkers={loadingProjectWorkers}
            loadingBudgetOptions={loadingBudgetOptions}
          />
        </Box>
      )
    }

    if (editSection === 'costCenters') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <CostCenterInformation
            {...props}
            currentEmployee={employee}
            resourceId={expensable?.id}
            asReviewer={asReviewer}
            resourceType={'expense'}
            documentType={documentType}
          />
        </Box>
      )
    }

    if (editSection === 'additionalInformation') {
      return (
        <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
          {backLink}
          <AdditionalInformation
            {...props}
            currentEmployee={employee}
            resourceType={'perdiem'}
            asReviewer={asReviewer}
            documentType={documentType}
            expensable={expensable}
          />
        </Box>
      )
    }
  }

  return (
    <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
      <BasicDetails expensable={expensable} />
      {!isNewExpense && isAutomatic && <TransactionsDetailsWidget expensable={expensable} />}
      {!isNewPerDiem && (canCreateExpensesForOthers || hasSubcategory) && (
        <Box ref={basicInformationRef}>
          <ExpenseInformation
            {...props}
            currentEmployee={employee}
            resourceType={'perdiem'}
            asReviewer={asReviewer}
            onEmployeeIdChange={onEmployeeIdChange}
            isAutomaticExpensable={isAutomatic}
            documentType={documentType}
            onEffectiveOnChange={onEffectiveOnChange}
          />
        </Box>
      )}

      {!isNewPerDiem && (
        <>
          <Box ref={tripInformationRef}>
            <TripInformation
              {...props}
              currentEmployee={employee}
              asReviewer={asReviewer}
              resourceType={'perdiem'}
              documentType={documentType}
              loading={false}
            />
          </Box>
          <Box ref={perDiemRatesRef}>
            <PerDiemRates
              {...props}
              currentEmployee={employee}
              asReviewer={asReviewer}
              resourceType={'perdiem'}
              documentType={documentType}
            />
          </Box>
        </>
      )}
      {isNewPerDiem && (
        <>
          <Box ref={tripInformationRef}>
            <Destinations expensable={expensable} readOnly={!isTripSectionEditable} />
          </Box>
          <Box ref={companyPaidRef}>
            <DailyConcepts
              expensable={expensable}
              onConceptsChange={handleConceptsChange}
              disabled={isRecalculating || !isTripSectionEditable}
            />
          </Box>
          <DailyBreakdown
            days={displayDays}
            totalAmountCents={displayTotalCents}
            currency={breakdownCurrency}
            locale={locale}
            isLoading={displayIsLoading}
            conceptExclusionsByDate={conceptExclusionsByDate}
          />
          <FormBlock
            title={i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_label')}
            subtitle={i18n.t(
              'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_placeholder'
            )}
          >
            <Controller
              control={control}
              name="perDiem.tripName"
              render={({ field }) => (
                <TextField
                  label={i18n.t(
                    'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_label'
                  )}
                  placeholder={i18n.t(
                    'expenses_v2.forms.germany_per_diem_modal.steps.step3.trip_name_placeholder'
                  )}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(value || null)}
                  readonly={!isTripSectionEditable}
                />
              )}
            />
          </FormBlock>
          {(canCreateExpensesForOthers || hasSubcategory) && (
            <Box ref={basicInformationRef}>
              <ExpenseInformation
                {...props}
                currentEmployee={employee}
                resourceType={'perdiem'}
                asReviewer={asReviewer}
                onEmployeeIdChange={onEmployeeIdChange}
                isAutomaticExpensable={isAutomatic}
                documentType={documentType}
                onEffectiveOnChange={onEffectiveOnChange}
              />
            </Box>
          )}
        </>
      )}
      <Box ref={budgetAndProjectRef}>
        <BudgetAndProject
          {...props}
          currentEmployee={employee}
          resourceType={'perdiem'}
          asReviewer={asReviewer}
          documentType={documentType}
          budgetOptions={budgetOptions}
          projectWorkers={projectWorkers}
          hasProjectsFeature={hasProjectsFeature}
          loadingProjectWorkers={loadingProjectWorkers}
          loadingBudgetOptions={loadingBudgetOptions}
        />
      </Box>
      <Box ref={costCenterInformationRef}>
        <CostCenterInformation
          {...props}
          currentEmployee={employee}
          resourceId={expensable?.id}
          asReviewer={asReviewer}
          resourceType={'expense'}
          documentType={documentType}
        />
      </Box>
      <Box ref={additionalInformationRef}>
        <AdditionalInformation
          {...props}
          currentEmployee={employee}
          resourceType={'perdiem'}
          asReviewer={asReviewer}
          documentType={documentType}
          expensable={expensable}
        />
      </Box>
      <Files
        {...props}
        currentEmployee={employee}
        fieldIdentifier="files"
        resourceType={'perdiem'}
        asReviewer={asReviewer}
        documentType={documentType}
        taxAgencyOrganization={taxAgencyOrganization}
        registerDropHandler={registerDropHandler}
      />
    </Box>
  )
}

export default NewPerDiemForm
